import { Card, Checkbox, Col, Form, Input, Row, Typography } from 'antd'
import { FC, useCallback, useRef } from 'react'
import { NamePath } from 'antd/es/form/interface'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import Header from './header'
import MaterialWidthStock from './form-items/material-width-stock'
import MaterialAmount from './form-items/material-amount'
import MaterialAction from './form-items/material-action'
import MaterialRemove from './form-items/material-remove'
import MaterialAdd from './form-items/material-add'
import { roundUpToTwoDecimals } from '../../../../../../helpers/round-up-to-two-decimals'

interface MaterialRequirementsProps {
    namePath: NamePath
    prefix?: NamePath
    minMaterialWidth?: number
    /** Все ширины типа без minWidth/standardOnly (доп. материалы). */
    unrestrictedWidths?: boolean
    necessary?: number
    initialDisabled?: boolean
}

type MaterialConfigurationValue = { amount?: number }

const MaterialRequirements: FC<MaterialRequirementsProps> = ({
    namePath,
    prefix = [],
    minMaterialWidth,
    unrestrictedWidths = false,
    necessary,
    initialDisabled = false,
}) => {
    const form = useFormInstance()
    const fullName = [...prefix, ...namePath]
    const materialTypeDocIdName = [...fullName, 'materialTypeDocId']
    const materialTypeNameName = [...fullName, 'materialTypeName']
    const materialInCuttingName = [...fullName, 'materialInCutting']
    const componentIdName = [...fullName, 'componentId']
    const configurationsName = [...namePath, 'configurations']
    const fullConfigurationsName = [...fullName, 'configurations']
    const initialConfigurationsCountRef = useRef<number | null>(null)

    if (initialConfigurationsCountRef.current === null) {
        const initialConfigurations = form.getFieldValue(fullConfigurationsName)
        initialConfigurationsCountRef.current = Array.isArray(initialConfigurations)
            ? initialConfigurations.length
            : 0
    }

    const materialTypeDocId = form.getFieldValue(materialTypeDocIdName)
    const validateRequiredMaterialAmount = useCallback(
        async (_rule: unknown, configurations: MaterialConfigurationValue[] = []) => {
            if (necessary == null) {
                return Promise.resolve()
            }

            const selected = configurations.reduce((sum, cur) => sum + (cur.amount || 0), 0)

            if (selected >= necessary) {
                return Promise.resolve()
            }

            const shortage = roundUpToTwoDecimals(necessary - selected)
            return Promise.reject(
                new Error(`Недостаточное количество, необходимо добрать: ${shortage} км`)
            )
        },
        [necessary]
    )

    return (
        <Card>
            <Header namePath={namePath} prefix={prefix} necessary={necessary} />
            <br />
            <Form.Item name={materialTypeDocIdName} hidden>
                <Input />
            </Form.Item>
            <Form.Item name={materialTypeNameName} hidden>
                <Input />
            </Form.Item>
            <Form.Item name={materialInCuttingName} valuePropName='checked' hidden>
                <Checkbox />
            </Form.Item>
            <Form.Item name={componentIdName} hidden>
                <Input />
            </Form.Item>

            <Form.List
                name={configurationsName}
                rules={[
                    {
                        validator: validateRequiredMaterialAmount,
                    },
                ]}
            >
                {(fields, { add, remove }, { errors }) => (
                    <>
                        <Row gutter={32}>
                            {fields.map(({ name: fieldName, key, ...rest }) => {
                                const isInitialConfiguration =
                                    initialDisabled &&
                                    Number(fieldName) < (initialConfigurationsCountRef.current || 0)

                                return (
                                    <Col sm={12} key={key}>
                                        <Form.Item name={[fieldName, 'componentId']} hidden>
                                            <Input />
                                        </Form.Item>

                                        <Row gutter={12}>
                                            <Col sm={9}>
                                                <MaterialWidthStock
                                                    materialTypeDocId={materialTypeDocId}
                                                    name={fieldName}
                                                    rest={rest}
                                                    minMaterialWidth={minMaterialWidth}
                                                    unrestrictedWidths={unrestrictedWidths}
                                                    disabled={isInitialConfiguration}
                                                />
                                            </Col>

                                            <Col sm={4}>
                                                <MaterialAmount
                                                    name={fieldName}
                                                    rest={rest}
                                                    materialTypeDocId={materialTypeDocId}
                                                    configurationsName={fullConfigurationsName}
                                                    disabled={isInitialConfiguration}
                                                />
                                            </Col>

                                            <Col sm={8}>
                                                <MaterialAction
                                                    name={fieldName}
                                                    rest={rest}
                                                    materialTypeDocId={materialTypeDocId}
                                                    configurationsName={fullConfigurationsName}
                                                    disabled={isInitialConfiguration}
                                                />
                                            </Col>

                                            <Col sm={3}>
                                                <MaterialRemove
                                                    name={fieldName}
                                                    remove={remove}
                                                    disabled={isInitialConfiguration}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                )
                            })}
                            <Col sm={12}>
                                <MaterialAdd add={add} />
                            </Col>
                        </Row>
                        <Typography.Text type='danger'>{errors[0]}</Typography.Text>
                    </>
                )}
            </Form.List>
        </Card>
    )
}

export default MaterialRequirements
